import { useParams, useRouteMatch } from 'react-router-dom';
import { Badge } from '@givebutter/primitives/badge';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { maskDecimal } from '@givebutter/util';
import { faCircleQuestion, faEllipsis, faHeart } from '@fortawesome/pro-regular-svg-icons';
import {
    faHeart as faHeartFilled,
    faMoneyBillTrendUp,
    faReceipt,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as DeleteTransactionDialog from 'assets/js/pages/transactions/DeleteTransactionDialog';
import { ReassignTransaction } from 'assets/js/pages/transactions/ReassignTransaction';
import * as RefundTransactionDialog from 'assets/js/pages/transactions/RefundTransactionDialog';
import { SupporterDetails } from 'assets/js/pages/transactions/SupporterDetails';
import * as TransactionActions from 'assets/js/pages/transactions/TransactionActions';
import { TransactionCampaign } from 'assets/js/pages/transactions/TransactionCampaign';
import { TransactionCustomFields } from 'assets/js/pages/transactions/TransactionCustomFields';
import { TransactionDedication } from 'assets/js/pages/transactions/TransactionDedication';
import { TransactionDonationMessage } from 'assets/js/pages/transactions/TransactionDonationMessage';
import { TransactionEditableDonationDate } from 'assets/js/pages/transactions/TransactionEditableDonationDate';
import { TransactionEditableInternalNote } from 'assets/js/pages/transactions/TransactionEditableInternalNote';
import { TransactionEditablePaymentMethod } from 'assets/js/pages/transactions/TransactionEditablePaymentMethod';
import { TransactionFunds } from 'assets/js/pages/transactions/TransactionFunds';
import { TransactionIntegrationStatusBadge } from 'assets/js/pages/transactions/TransactionIntegrationStatusBadge';
import { TransactionIntegrations } from 'assets/js/pages/transactions/TransactionIntegrations';
import { TransactionLineItems } from 'assets/js/pages/transactions/TransactionLineItems';
import { TransactionOther } from 'assets/js/pages/transactions/TransactionOther';
import { TransactionQuickBooksStatusBadge } from 'assets/js/pages/transactions/TransactionQuickBooksStatusBadge';
import { TransactionRecurringPlanBadge } from 'assets/js/pages/transactions/TransactionRecurringPlanBadge';
import { TransactionStatusBadge } from 'assets/js/pages/transactions/TransactionStatusBadge';
import { TransactionUtm } from 'assets/js/pages/transactions/TransactionUtm';
import { useToggleAcknowledge } from 'assets/js/pages/transactions/useToggleAcknowledge';
import * as Breadcrumbs from 'components/breadcrumbs';
import * as Drawer from 'components/drawer';
import { DrawerIncrementor } from 'components/drawer/DrawerIncrementor';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Tooltip from 'components/tooltip';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { rawDateFromServer } from 'helpers/dates';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';

const TransactionDrawer = () => {
    const match = useRouteMatch();
    const { plan_id, ticket_id, bundle_id } = useParams<{
        plan_id?: string;
        ticket_id?: string;
        bundle_id?: string;
    }>();
    const { quickBooksIntegration = false } = useFlags();
    const { accountId } = useCurrentAccount();
    const currencySymbol = useCurrencySymbol();
    const { data: transaction } = useCurrentTransactionOrById();
    const { mutate: toggleAcknowledge } = useToggleAcknowledge();
    const { timezone } = useCurrentAccount();
    const isOfflineCashOrCheck =
        transaction?.service === 'offline' &&
        (transaction?.method === 'cash' || transaction?.method === 'check');

    const { entityType, entityId } = getEntityTypeAndId(plan_id, ticket_id, bundle_id);
    const { text, url } = getBreadcrumbDetails(entityType, entityId, accountId);
    const dateFormat = isOfflineCashOrCheck ? DateTime.DATE_FULL : DateTime.DATETIME_FULL;
    const isIndex = match.path.includes('/accounts/:account_id/transactions');

    return (
        <>
            <Drawer.Header>
                <Drawer.Title className="sr-only">Transaction #{transaction?.number}</Drawer.Title>
                <div className={'flex w-full flex-col gap-4'}>
                    {entityType && (
                        <div className={'flex'}>
                            <Breadcrumbs.Root>
                                <Breadcrumbs.Crumb to={url}>{text}</Breadcrumbs.Crumb>
                                <Breadcrumbs.Crumb to={match.url}>
                                    Transaction #{transaction?.number}
                                </Breadcrumbs.Crumb>
                            </Breadcrumbs.Root>
                        </div>
                    )}
                    <div className={'relative flex flex-col gap-4'}>
                        <BoxIcon size="lg">
                            <FontAwesomeIcon size={'lg'} icon={faReceipt} />
                        </BoxIcon>
                        <div>
                            <div className={'flex items-center gap-2'}>
                                <p className={'text-2xl font-medium'}>
                                    {currencySymbol}
                                    {maskDecimal(transaction.total_display)}
                                </p>
                                <TransactionRecurringPlanBadge transaction={transaction} />
                                <TransactionStatusBadge transaction={transaction} />
                                <TransactionIntegrationStatusBadge />
                                {quickBooksIntegration && <TransactionQuickBooksStatusBadge />}
                                {transaction.is_external && <Badge color="warning">External</Badge>}
                            </div>
                            <div className={'flex items-center gap-2'}>
                                <p className={'text-sm text-gray-600'}>#{transaction.number}</p>
                                <Separator
                                    className={'!h-4 bg-gray-300'}
                                    orientation={'vertical'}
                                />
                                <p className={'text-sm text-gray-600'}>
                                    {transaction.transacted_at
                                        ? rawDateFromServer(transaction.transacted_at)
                                              .setZone(timezone)
                                              .toLocaleString(dateFormat)
                                        : ''}
                                </p>
                                {!!transaction.upsells?.convert_to_plan?.upsell_accepted && (
                                    <>
                                        <Separator
                                            className={'!h-4 bg-gray-300'}
                                            orientation={'vertical'}
                                        />
                                        <Tooltip.Root>
                                            <Tooltip.Trigger>
                                                <FontAwesomeIcon
                                                    className={'text-brand-violet-primary'}
                                                    icon={faMoneyBillTrendUp}
                                                />
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content inverse={true}>
                                                    <p>
                                                        This donor changed their contribution from
                                                        one-time to a monthly donation in response
                                                        to the upsell screen.
                                                    </p>
                                                    <Tooltip.Arrow inverse={true} />
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className={'absolute right-0 top-0 flex items-center gap-2'}>
                            {isIndex && <DrawerIncrementor resource="transactions" />}
                            <Tooltip.Root>
                                <Tooltip.Trigger asChild={true}>
                                    <Button
                                        size={'sm'}
                                        variant={'outline'}
                                        color={'neutral'}
                                        className={'flex items-center gap-2'}
                                        onClick={() => toggleAcknowledge(transaction)}
                                    >
                                        <p>Acknowledge</p>
                                        <FontAwesomeIcon
                                            className={
                                                transaction.acknowledged_at ? 'text-red-500' : ''
                                            }
                                            icon={
                                                transaction.acknowledged_at
                                                    ? faHeartFilled
                                                    : faHeart
                                            }
                                        />
                                    </Button>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse={true}>
                                        <p>
                                            {transaction.acknowledged_at
                                                ? 'Unacknowledge'
                                                : 'Acknowledge'}{' '}
                                            transaction for internal tracking. This doesn't alter
                                            the transaction, notify the donor, or become visible to
                                            the public.
                                        </p>
                                        <Tooltip.Arrow inverse={true} />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                            <DropdownMenu.Root>
                                <DropdownMenu.Trigger asChild={true}>
                                    <Button
                                        size={'sm'}
                                        variant={'outline'}
                                        color={'neutral'}
                                        shape={'square'}
                                        aria-label="More actions"
                                    >
                                        <FontAwesomeIcon icon={faEllipsis} />
                                    </Button>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content sideOffset={4} align={'end'}>
                                    {!transaction.is_external && (
                                        <TransactionActions.Root transaction={transaction}>
                                            <TransactionActions.Resend />
                                        </TransactionActions.Root>
                                    )}
                                    <RefundTransactionDialog.Root>
                                        <RefundTransactionDialog.Trigger
                                            type={'reset'}
                                            asChild={true}
                                        >
                                            <DropdownMenu.Item
                                                disabled={!transaction.can_refund}
                                                className={'flex items-center gap-1'}
                                                onSelect={e => e.preventDefault()}
                                            >
                                                Refund
                                                {!transaction.can_refund && (
                                                    <Tooltip.Root>
                                                        <Tooltip.Trigger>
                                                            <FontAwesomeIcon
                                                                icon={faCircleQuestion}
                                                            />
                                                        </Tooltip.Trigger>
                                                        <Tooltip.Portal>
                                                            <Tooltip.Content inverse={true}>
                                                                <p>
                                                                    This payment can not be refunded
                                                                    because either it is was already
                                                                    refunded, a payment accepted
                                                                    outside of Givebutter, or your
                                                                    campaign does not have
                                                                    sufficient balance to cover the
                                                                    refund.
                                                                </p>
                                                                <Tooltip.Arrow inverse={true} />
                                                            </Tooltip.Content>
                                                        </Tooltip.Portal>
                                                    </Tooltip.Root>
                                                )}
                                            </DropdownMenu.Item>
                                        </RefundTransactionDialog.Trigger>
                                    </RefundTransactionDialog.Root>
                                    <ReassignTransaction />
                                    <DeleteTransactionDialog.Root>
                                        <DeleteTransactionDialog.Trigger
                                            type={'reset'}
                                            asChild={true}
                                        >
                                            <DropdownMenu.Item
                                                disabled={!transaction.can_delete}
                                                onSelect={e => e.preventDefault()}
                                            >
                                                Delete
                                            </DropdownMenu.Item>
                                        </DeleteTransactionDialog.Trigger>
                                    </DeleteTransactionDialog.Root>
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </div>
                    </div>
                </div>
            </Drawer.Header>
            <Drawer.Body>
                <div className={'flex flex-col gap-6'}>
                    <div className={'flex flex-col gap-4'}>
                        {transaction.contact ? (
                            <SupporterDetails contact={transaction.contact} />
                        ) : null}
                        {transaction && <TransactionCampaign transaction={transaction} />}
                        {transaction.service === 'offline' && (
                            <div className={'w-fit'}>
                                <TransactionEditableDonationDate />
                            </div>
                        )}
                        <TransactionEditablePaymentMethod />
                    </div>
                    <TransactionLineItems />
                    {(transaction.giving_space?.message || transaction.giving_space?.visual) && (
                        <TransactionDonationMessage />
                    )}

                    <TransactionEditableInternalNote />
                    <TransactionDedication />
                    <TransactionFunds />
                    <TransactionCustomFields />
                    {transaction.sources.length > 0 && <TransactionUtm />}
                    {transaction.external_id && <TransactionOther />}
                    {quickBooksIntegration && <TransactionIntegrations />}
                </div>
            </Drawer.Body>
            <Drawer.Footer className={'sm:hidden'}>
                <Drawer.Cancel asChild={true}>
                    <Button variant={'outline'} color={'neutral'}>
                        Close
                    </Button>
                </Drawer.Cancel>
            </Drawer.Footer>
        </>
    );
};

const getEntityTypeAndId = (plan_id?: string, ticket_id?: string, bundle_id?: string) => {
    let entityType: 'plan' | 'ticket' | 'bundle' | null = null;
    let entityId: string | undefined;

    if (plan_id) {
        entityType = 'plan';
        entityId = plan_id;
    } else if (ticket_id) {
        entityType = 'ticket';
        entityId = ticket_id;
    } else if (bundle_id) {
        entityType = 'bundle';
        entityId = bundle_id;
    }

    return { entityType, entityId };
};

const getBreadcrumbDetails = (
    entityType: string | null,
    entityId: string | undefined,
    accountId: number,
) => {
    switch (entityType) {
        case 'plan':
            return {
                text: `Recurring Plan #${entityId}`,
                url: `/accounts/${accountId}/plans/${entityId}`,
            };
        case 'ticket':
            return {
                text: `Ticket #${entityId}`,
                url: `/accounts/${accountId}/tickets/${entityId}`,
            };
        case 'bundle':
            return {
                text: `Ticket Bundle #${entityId}`,
                url: `/accounts/${accountId}/tickets/ticket-bundles/${entityId}`,
            };
        default:
            return { text: '', url: '' };
    }
};

export { TransactionDrawer };
